import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';

/**
 * Platform administration panel (super-admin only).
 * Lists every tenant, shows platform stats, and lets the app owner suspend /
 * reactivate a company or change its plan. Gated by RequirePlatformAdmin.
 */
const StatCard = ({ label, value }) => (
  <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
    <div className="text-2xl font-semibold text-white">{value}</div>
    <div className="text-sm text-gray-400 mt-1">{label}</div>
  </div>
);

const AdminPage = () => {
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, c, p] = await Promise.all([
        api.get('/api/admin/stats'),
        api.get('/api/admin/companies'),
        api.get('/api/admin/plans'),
      ]);
      setStats(s.data);
      setCompanies(c.data);
      setPlans(p.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleSuspend = async (company) => {
    setBusyId(company.id);
    try {
      const action = company.is_active ? 'suspend' : 'reactivate';
      await api.post(`/api/admin/companies/${company.id}/${action}`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const changePlan = async (company, planSlug) => {
    setBusyId(company.id);
    try {
      await api.put(`/api/admin/companies/${company.id}/plan`, { plan_slug: planSlug });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to change plan');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="text-gray-200">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Platform Administration</h1>
          <p className="text-gray-400 text-sm mt-1">Manage every company on SageMindLabs.</p>
        </div>
        <button
          onClick={load}
          className="px-3 py-2 text-sm rounded-md bg-gray-700 hover:bg-gray-600 text-white"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-900/40 border border-red-700 text-red-200 px-4 py-3" role="alert">
          {error}
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatCard label="Companies" value={stats.total_companies} />
          <StatCard label="Active" value={stats.active_companies} />
          <StatCard label="Suspended" value={stats.suspended_companies} />
          <StatCard label="Users" value={stats.total_users} />
          <StatCard label="Platform admins" value={stats.total_platform_admins} />
        </div>
      )}

      <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-900/60 text-gray-400">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Company</th>
                <th className="text-left px-4 py-3 font-medium">Members</th>
                <th className="text-left px-4 py-3 font-medium">Plan</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">Loading…</td></tr>
              )}
              {!loading && companies.map((c) => (
                <tr key={c.id} className="hover:bg-gray-700/30">
                  <td className="px-4 py-3">
                    <div className="text-white font-medium">{c.name}</div>
                    <div className="text-gray-500 text-xs">{c.slug}</div>
                  </td>
                  <td className="px-4 py-3">{c.member_count}</td>
                  <td className="px-4 py-3">
                    <select
                      value={c.plan_slug || ''}
                      disabled={busyId === c.id}
                      onChange={(e) => changePlan(c, e.target.value)}
                      className="bg-gray-900 border border-gray-600 rounded px-2 py-1 text-gray-100"
                      aria-label={`Plan for ${c.name}`}
                    >
                      {plans.map((p) => (
                        <option key={p.slug} value={p.slug}>{p.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    {c.is_active ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-900/50 text-green-300">Active</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-red-900/50 text-red-300">Suspended</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => toggleSuspend(c)}
                      disabled={busyId === c.id}
                      className={`px-3 py-1.5 text-xs rounded-md font-medium disabled:opacity-50 ${
                        c.is_active
                          ? 'bg-red-700 hover:bg-red-600 text-white'
                          : 'bg-green-700 hover:bg-green-600 text-white'
                      }`}
                    >
                      {c.is_active ? 'Suspend' : 'Reactivate'}
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && companies.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">No companies.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
